/**
 * Which fields a `ValidationPipe` 400 is complaining about.
 *
 * Nest answers `{"message":["phone must match /^[0-9]{10}$/ regular expression"],…}` — one sentence per
 * broken constraint, each **starting with the property it is about**. That first word is the only part of
 * the body a candidate-facing screen can use: the sentence itself is untranslated English written for a
 * developer, so the screen looks the field up in its own dictionary and this text never reaches a person.
 *
 * A message it cannot read yields no field, and the screen falls back to what it can honestly say —
 * painting the wrong control red is worse than painting none (convention skill § 6, *parse what the api
 * returns*). A single-string `message` is accepted too: Nest sends one when the exception is not the
 * validation pipe's.
 *
 * Shared by the two requests whose 400 a candidate can act on: the profile save (`candidate.ts`) and the
 * identity lookup (`identityLookup.ts`, ISS-147).
 */
export function refusedFieldsIn(body: unknown): readonly string[] {
  if (typeof body !== 'object' || body === null) return [];
  const { message } = body as { message?: unknown };
  const messages =
    typeof message === 'string' ? [message] : Array.isArray(message) ? (message as unknown[]) : [];
  const named = messages
    .filter((line): line is string => typeof line === 'string')
    .map(fieldNamed)
    .filter((name) => name !== '');
  return [...new Set(named)];
}

/** The leading identifier of a constraint message, stripped of the punctuation a list of fields carries. */
function fieldNamed(message: string): string {
  return (message.trim().split(/[\s,]/)[0] ?? '').replace(/[^A-Za-z0-9]/g, '');
}
